import * as React from "react";

import { rewriteThreeEAppStripImageUrls } from "./ThreeEAppStripAssetUrls";

class ThreeEAppStripMenuController {
    private activeTrigger: HTMLButtonElement | null = null;
    private initialized = false;
    private loaded = false;
    private loadingPromise: Promise<void> | null = null;
    private menuHost: HTMLDivElement | null = null;

    public toggle = async (trigger: HTMLButtonElement) => {

        if (trigger.getAttribute("aria-expanded") === "true") {
            this.close();
            return;
        }

        this.initialize();
        this.setActiveTrigger(trigger);

        if (!this.loaded) {
            this.loadingPromise ??= this.load();
            await this.loadingPromise;
        }

        if (this.loaded && (this.activeTrigger === trigger)) {
            this.open(trigger);
        }
    }

    private initialize = () => {
        if (this.initialized) {
            return;
        }

        this.menuHost = document.createElement("div");
        this.menuHost.id = "threeEAppStripReactMenuHost";
        this.menuHost.className = "three-e-app-strip-menu-host";
        document.body.appendChild(this.menuHost);

        document.addEventListener("click", this.document_click);
        document.addEventListener("scroll", this.document_scroll, true);
        window.addEventListener("resize", this.window_resize);

        this.initialized = true;
    }

    private load = async () => {
        try {
            this.ensureCss();

            const response = await fetch("/three-e-appstrip", {
                method: "GET",
                credentials: "same-origin"
            });

            if (!response.ok) {
                throw new Error("Failed to load 3E AppStrip. Status: " + response.status);
            }

            const responseDocument = new DOMParser().parseFromString(await response.text(), "text/html");
            const appStrip = responseDocument.getElementById("appStrip");

            if ((appStrip === null) || (this.menuHost === null)) {
                throw new Error("The 3E AppStrip response did not contain the expected markup.");
            }

            rewriteThreeEAppStripImageUrls(appStrip);
            this.menuHost.innerHTML = appStrip.outerHTML;
            this.hideUpstreamTrigger();
            this.loaded = true;
        } catch (error) {
            console.error(error);
            this.close();
        } finally {
            this.loadingPromise = null;
        }
    }

    private ensureCss = () => {
        if (document.querySelector('link[data-three-e-app-strip-css="true"]') !== null) {
            return;
        }

        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "/three-e-appstrip-assets/appStrip.css";
        link.setAttribute("data-three-e-app-strip-css", "true");
        document.head.appendChild(link);
    }

    private hideUpstreamTrigger = () => {
        const subMenu = this.menuHost?.querySelector<HTMLElement>("#sub-menu");
        const menuItem = subMenu?.parentElement;

        if ((subMenu === null) || (subMenu === undefined) || (menuItem === null) || (menuItem === undefined)) {
            return;
        }

        Array.from(menuItem.children).forEach(child => {
            if (child !== subMenu) {
                (child as HTMLElement).style.display = "none";
            }
        });
    }

    private setActiveTrigger = (trigger: HTMLButtonElement) => {
        if (this.activeTrigger !== null) {
            this.activeTrigger.setAttribute("aria-expanded", "false");
        }

        this.activeTrigger = trigger;
    }

    private open = (trigger: HTMLButtonElement) => {
        const subMenu = this.menuHost?.querySelector<HTMLElement>("#sub-menu");
        if (subMenu === null || subMenu === undefined) {
            return;
        }

        const triggerBounds = trigger.getBoundingClientRect();
        subMenu.style.position = "fixed";
        subMenu.style.left = Math.max(8, triggerBounds.left) + "px";
        subMenu.style.top = (triggerBounds.bottom + 8) + "px";
        subMenu.style.margin = "0";
        subMenu.style.zIndex = "1100";
        subMenu.style.display = "block";
        trigger.setAttribute("aria-expanded", "true");
    }

    private close = () => {
        const subMenu = this.menuHost?.querySelector<HTMLElement>("#sub-menu");
        if ((subMenu !== null) && (subMenu !== undefined)) {
            subMenu.style.display = "none";
        }

        if (this.activeTrigger !== null) {
            this.activeTrigger.setAttribute("aria-expanded", "false");
        }
    }

    private document_click = (event: MouseEvent) => {
        const target = event.target;
        if (!(target instanceof Node)) {
            return;
        }

        if ((this.activeTrigger?.contains(target) ?? false) ||
            (this.menuHost?.contains(target) ?? false)) {
            return;
        }

        this.close();
    }

    private document_scroll = () => {
        this.close();
    }

    private window_resize = () => {
        this.close();
    }
}

const threeEAppStripMenuController = new ThreeEAppStripMenuController();

export default class ThreeEAppStrip extends React.PureComponent {
    private triggerRef = React.createRef<HTMLButtonElement>();

    public render() {
        return (
            <button
                ref={this.triggerRef}
                type="button"
                className="three-e-app-strip-button"
                aria-label="Open 3E product switcher"
                aria-expanded="false"
                onClick={this.button_click}>
                <img
                    src="/three-e-appstrip-assets/images/Waffle.svg"
                    width="16"
                    height="16"
                    alt="" />
            </button>
        );
    }

    private button_click = () => {
        if (this.triggerRef.current !== null) {
            void threeEAppStripMenuController.toggle(this.triggerRef.current);
        }
    }
}
